import React, { useEffect } from 'react';
import {
  X,
  ShoppingBag,
  MapPin,
  Bike,
  UtensilsCrossed,
  FileDown,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import {
  formatMoney,
  formatDate,
  WEEKDAYS_PT,
  pedidoCanalMeta,
  getOrderStatusLabel,
  getClienteDisplayName,
} from '../utils/crmDashboardHelpers';

export function OrderModal({
  order,
  cliente,
  details,
  detailsLoading = false,
  detailsError = '',
  onClose,
}) {
  if (!order) return null;

  const canal = pedidoCanalMeta(order.type);
  const CanalIcon = canal.icon === 'bike' ? Bike : canal.icon === 'salao' ? UtensilsCrossed : ShoppingBag;
  const value = Number(order.value || 0);
  const orderId = order.id ? String(order.id) : '';
  const orderIdShort = orderId ? orderId.slice(0, 8) : '#';
  const note = String(order.note || '').trim();
  const fallbackItemCount = Number(order.itemCount ?? order.items ?? 0);
  const detailedItems = Array.isArray(details?.items) ? details.items : [];
  const status = getOrderStatusLabel(order);
  const paymentMethod = order.paymentMethod || 'N/D';
  const dateObj = order.date ? new Date(order.date) : null;
  const validDate = dateObj && !Number.isNaN(dateObj.getTime());
  const weekday = validDate ? WEEKDAYS_PT[dateObj.getDay()] : '';
  const dateStr = validDate ? formatDate(order.date) : 'N/D';

  return (
    <div className="crm-modal active" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="crm-modal-card crm-modal-order" role="dialog" aria-modal="true" aria-label="Detalhes do pedido">
        <div className="crm-modal-head">
          <div className="crm-modal-title">
            <ShoppingBag size={16} />
            Detalhes do pedido
          </div>
          <button
            type="button"
            className="crm-modal-close"
            onClick={onClose}
            aria-label="Fechar"
            title="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="crm-modal-hero">
          <span className={`crm-badge crm-canal-badge crm-canal-${canal.tone}`}>
            <CanalIcon size={11} />
            {canal.label}
          </span>
          <div className={`crm-modal-value ${!value ? 'empty' : ''}`}>
            {formatMoney(value)}
          </div>
          <div className="crm-modal-when">
            {weekday ? `${weekday}, ` : ''}{dateStr}
          </div>
        </div>

        <div className="crm-modal-fields">
          <div className="crm-modal-field">
            <span className="crm-modal-field-label">Cliente</span>
            <span className="crm-modal-field-value">{getClienteDisplayName(cliente)}</span>
          </div>
          <div className="crm-modal-field">
            <span className="crm-modal-field-label">Pedido</span>
            <span className="crm-modal-field-value mono" title={orderId}>#{orderIdShort}</span>
          </div>
          <div className="crm-modal-field stacked">
            <span className="crm-modal-field-label">Itens</span>
            <div className="crm-modal-field-value full">
              {detailsLoading ? (
                <span className="crm-inline-loading">Carregando itens...</span>
              ) : detailsError ? (
                <span className="crm-inline-error">{detailsError}</span>
              ) : detailedItems.length ? (
                <div className="crm-order-items-list">
                  {detailedItems.map((item, index) => (
                    <div className="crm-order-item-row" key={`${item.name}-${index}`}>
                      <span className="crm-order-item-qty">{Number(item.quantity || 1)}x</span>
                      <span>{item.name}</span>
                    </div>
                  ))}
                </div>
              ) : fallbackItemCount ? (
                `${fallbackItemCount} ${fallbackItemCount === 1 ? 'item' : 'itens'}`
              ) : (
                '—'
              )}
            </div>
          </div>
          <div className="crm-modal-field">
            <span className="crm-modal-field-label">Status</span>
            <span className="crm-modal-field-value">{status}</span>
          </div>
          <div className="crm-modal-field">
            <span className="crm-modal-field-label">Pagamento</span>
            <span className="crm-modal-field-value">{paymentMethod}</span>
          </div>
        </div>

        <div className="crm-modal-notes">
          <span className="crm-modal-field-label">Observações</span>
          {note ? (
            <p className="crm-modal-note-text">{note}</p>
          ) : (
            <p className="crm-modal-note-text empty">Sem observações registradas.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function AddressModal({ open, addresses, loading, error, onClose }) {
  if (!open) return null;

  const list = Array.isArray(addresses) ? addresses : [];

  return (
    <div className="crm-modal active" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="crm-modal-card crm-modal-addresses" role="dialog" aria-modal="true" aria-label="Endereços do cliente">
        <div className="crm-modal-head">
          <div className="crm-modal-title">
            <MapPin size={16} />
            Endereços do cliente
          </div>
          <button
            type="button"
            className="crm-modal-close"
            onClick={onClose}
            aria-label="Fechar"
            title="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="crm-loading compact">Carregando endereços...</div>
        ) : error ? (
          <div className="crm-alert">{error}</div>
        ) : list.length ? (
          <div className="crm-address-list">
            {list.map((address, index) => (
              <div className="crm-address-card" key={address.id || `${address.address}-${index}`}>
                <div className="crm-address-card-head">
                  <MapPin size={13} />
                  <strong>{index === 0 ? 'Último endereço usado' : `Endereço ${index + 1}`}</strong>
                </div>
                <p>{address.address}</p>
                <div className="crm-address-meta">
                  {address.complement && <span>Compl.: {address.complement}</span>}
                  {address.reference && <span>Ref.: {address.reference}</span>}
                  {address.zipCode && <span>CEP {address.zipCode}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="crm-empty">Nenhum endereço registrado.</p>
        )}
      </div>
    </div>
  );
}

export function ExportModal({ open, exporting, onClose, onExport }) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !exporting) onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [exporting, onClose, open]);

  if (!open) return null;

  return (
    <div className="crm-modal active" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !exporting) onClose();
    }}>
      <div
        className="crm-modal-card crm-export-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="crm-export-title"
        aria-describedby="crm-export-description"
      >
        <div className="crm-modal-head">
          <div className="crm-modal-title" id="crm-export-title">
            <FileDown size={17} />
            Exportar dados
          </div>
          <button
            type="button"
            className="crm-modal-close"
            onClick={onClose}
            disabled={exporting}
            aria-label="Fechar"
            title="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <p className="crm-export-description" id="crm-export-description">
          Escolha o formato do arquivo. Os filtros aplicados serão mantidos na exportação.
        </p>

        <div className="crm-export-options">
          <button
            type="button"
            className="crm-export-choice"
            onClick={() => onExport('pdf')}
            disabled={exporting}
          >
            <span className="crm-export-choice-icon is-pdf"><FileText size={24} /></span>
            <span>
              <strong>Exportar em PDF</strong>
              <small>Relatório pronto para visualizar, imprimir ou compartilhar.</small>
            </span>
          </button>
          <button
            type="button"
            className="crm-export-choice"
            onClick={() => onExport('csv')}
            disabled={exporting}
          >
            <span className="crm-export-choice-icon is-csv"><FileSpreadsheet size={24} /></span>
            <span>
              <strong>Exportar em CSV</strong>
              <small>Dados organizados para abrir no Excel ou em outra planilha.</small>
            </span>
          </button>
        </div>

        {exporting && <p className="crm-export-progress" role="status">Gerando arquivo...</p>}
      </div>
    </div>
  );
}
